import {useMemo} from 'react';

import {RolesAccount, RoleType} from '@/app/auth/constants/AuthConstants';

import {
  androidVersionMatch,
  iosVersionMatch,
  isAndroid,
  isIOS,
  MIN_COMPATIBLE_ANDROID_VERSION,
  MIN_COMPATIBLE_IOS_VERSION,
} from '../models/deviceCompatibility';

const VERSION_PARSE_RADIX = 10;

interface UseShowCompatibilityBannerParams {
  isLoadingFeatureFlags: boolean;
  flagsMap?: {onboardingV5Enabled?: boolean};
  role: RoleType;
  isMobile: boolean;
}

// Decides whether to warn the user their device/OS version is too old for
// the onboarding v5 experience.
export function useShowCompatibilityBanner({
  isLoadingFeatureFlags,
  flagsMap,
  role,
  isMobile,
}: UseShowCompatibilityBannerParams): boolean {
  return useMemo(() => {
    if (isLoadingFeatureFlags || !flagsMap?.onboardingV5Enabled) return false;

    if (role === RolesAccount.TEEN) {
      return false;
    }

    if (!isMobile) {
      return true;
    }

    const iosVersion = iosVersionMatch
      ? parseInt(iosVersionMatch[1], VERSION_PARSE_RADIX)
      : null;
    const androidVersion = androidVersionMatch
      ? parseInt(androidVersionMatch[1], VERSION_PARSE_RADIX)
      : null;

    if (isIOS && iosVersion !== null) {
      return iosVersion < MIN_COMPATIBLE_IOS_VERSION;
    }

    if (isAndroid && androidVersion !== null) {
      return androidVersion < MIN_COMPATIBLE_ANDROID_VERSION;
    }

    return true;
  }, [isLoadingFeatureFlags, flagsMap, role, isMobile]);
}
